(ns ui.editors.entity-creator.associations
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entity-creator.utils :as ecu]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.components.select.select :as select]
   [ui.components.sortable-list.table :as sortable-table-list]
   [ui.editors.entity-creator.copy-config :as copy-config]
   [ui.editors.entity-creator.event]
   [ui.editors.entity-creator.model :as m]
   [ui.editors.entity-creator.utils :as u]
   [ui.util :as uu]))

(defn init-stages!
  [{:keys [state stages]}]
  (let [stage-values (->> stages
                          (mapv :db/id)
                          (mapv (fn [id]
                                  [id :optional]))
                          (into {}))]
    (swap! state
           (fn [s]
             (-> s
                 (assoc :stages stage-values)
                 (dissoc :association :prefill))))))

(defn set-and-inherit!
  [{:keys [state stages stage new-setting]}]
  (let [{:keys [db/id]} stage
        stage-ord (:common/ord stage)
        after (->> stages
                   (filter (fn [{:keys [common/ord]}]
                             (< stage-ord ord)))
                   (map (fn [{:keys [db/id]}]
                          [id new-setting]))
                   (into {}))
        stages-settings (assoc after id new-setting)]
    (swap! state update :stages merge stages-settings)))

(defn person-association-popover
  [{:keys [state close-popover!]}]
  (let [stages @(rf/subscribe [m/stages])
        config-path (:associations-config-path
                     (ecu/resolve-config-paths
                      {:entity-type @(rf/subscribe [m/entity-type])
                       :target-type @(rf/subscribe [m/target-type])
                       :pipeline @(rf/subscribe [m/pipeline])}))
        config @(rf/subscribe [m/edited-config {:config-path config-path}])
        associations (:associations config)
        current-associations-set (->> associations
                                      (mapv :association-key)
                                      (into #{}))
        selected-association (:association @state)
        selected-prefill (:prefill @state)
        stages-config (:stages @state)
        association-options @(rf/subscribe [m/available-association-options])
        as (->> association-options
                (remove (fn [{:keys [value]}]
                          (contains? current-associations-set value))))
        prefill-options (->> association-options
                             (filter (fn [{:keys [value]}]
                                       (contains? current-associations-set value)))
                             (into [{:value nil :label (loc/tr :none)}]))]
    [:div
     {:class (uu/cm "w-[540px]")}
     [:div
      {:class (uu/cm "font-bold mb-1")}
      (loc/tr :creator-config/add-new-association)]
     [:div
      [:div
       {:class (uu/cm "mb-1")}
       (loc/tr :creator-config/select-association-type)]
      [select/view
       {:value selected-association
        :placeholder (loc/tr :creator-config/select-person-association)
        :options as
        :on-change (fn [association]
                     (swap! state assoc :association association))}]]
     [:div
      [:div
       {:class (uu/cm "font-bold mb-1 mt-2")}
       (loc/tr :creator-config/stage-requirements)]
      [:div
       {:class (uu/cm "flex flex-col gap-2")}
       (doall
        (for [stage-pair (partition-all 2 2 stages)]
          ^{:key (-> stage-pair first :db/id str)}
          [:div
           {:class (uu/cm "flex justify-between gap-3")}
           (doall
            (for [{:keys [db/id] :as stage} stage-pair
                  :let [setting (get-in @state [:stages id])
                        disabled? (u/inherited-mandatory? stages-config stages stage)]]
              ^{:key (str id)}
              [:div
               {:class "w-full flex flex-col justify-end"}
               [:div
                {:class (uu/cm "my-1")}
                (entity/display-string stage)]
               [:div
                {:class (uu/cm (when disabled?
                                 "text-disabled-light-text cursor-not-allowed"))}
                [select/view
                 {:value setting
                  :placeholder (loc/tr :select)
                  ;;TODO UI doesn't indicate disabled => use data-input(?)
                  ;;     or add manual class-names(?)
                  ;;     + add tooltip "inherited from earlier stage" etc
                  :disabled? disabled?
                  :options (->> [:mandatory
                                 :optional
                                 :not-shown]
                                (mapv (fn [k]
                                        {:value k
                                         :label (loc/tr k)})))
                  :on-change (fn [setting]
                               (set-and-inherit! {:state state
                                                  :stages stages
                                                  :stage stage
                                                  :new-setting setting}))}]]]))]))]]
     [:div
      [:div
       {:class (uu/cm "font-bold my-2")}
       (loc/tr :creator-config/field-prefillment)]
      [:div
       [:div
        {:class (uu/cm "mb-1")}
        (loc/tr :creator-config/prefilled-from)]
       [select/view
        {:value selected-prefill
         :placeholder (loc/tr :none)
         :options prefill-options
         :on-change (fn [val]
                      (if val
                        (swap! state assoc :prefill val)
                        (swap! state dissoc :prefill)))}]]]
     [:div
      {:class (uu/cm "flex justify-between mt-2")}
      [button/secondary-hollow
       {:on-click close-popover!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:on-click (fn [_]
                    (rf/dispatch [m/add-association
                                  {:association selected-association
                                   :config-path config-path
                                   :stages-config (:stages @state)
                                   :prefill selected-prefill}])
                    (close-popover!))
        :disabled? (not selected-association)}
       (loc/tr :add-association)]]]))

(defn stage-config-component
  [{:keys [stage-config]}]
  (let [{:keys [setting non-editable?]} stage-config]
    [:td
     {:class (uu/cm "text-center")}
     (if non-editable?
       [tooltip/view
        {:trigger-class (uu/cm "text-disabled-regular-text")}
        [:div
         (loc/tr (:setting stage-config))]
        (loc/tr :creator-config/inherited-association-setting)]
       [:div
        (loc/tr setting)])]))

(defn association-stages-config
  [{:keys [_index association-config _config-path _entity-type dropdown-container]}]
  (let [stages @(rf/subscribe [m/stages])
        #_#_update-config! (fn [{:keys [index stage-id]} new-setting]
                             (rf/dispatch
                              [m/update-field-config
                               {:setting new-setting
                                :index   index}]))
        stages-config @(rf/subscribe [m/association-stages-config
                                      {:association-config association-config}])]
    [:<>
     (doall
      (for [{:keys [db/id] :as stage} stages
            :let [stage-config (get stages-config id)]]
        ^{:key (str "stage-config-" id)}
        [stage-config-component
         {:dropdown-container dropdown-container
          #_#_:update-config!     (r/partial update-config!
                                             {:index        index
                                              :config-path  config-path
                                              :field-config field-config
                                              :stage-id     id
                                              :stage-config stage-config})
          :stage              stage
          :stage-config       stage-config}]))]))

(defn prefill-popover
  [{:keys [state association-config index close-popover!]}]
  (let [;;stages @(rf/subscribe [m/stages])
        config-path (:associations-config-path
                     (ecu/resolve-config-paths
                      {:entity-type @(rf/subscribe [m/entity-type])
                       :target-type @(rf/subscribe [m/target-type])
                       :pipeline @(rf/subscribe [m/pipeline])}))
        config @(rf/subscribe [m/edited-config {:config-path config-path}])
        current-association-key (:association-key association-config)
        current-prefill (:prefill-from association-config)
        associations (:associations config)
        current-associations-set (->> associations
                                      (mapv :association-key)
                                      (into #{}))
        selected-prefill (:prefill @state)
        ;;stages-config (:stages @state)
        association-options @(rf/subscribe [m/available-association-options])
        ;; note: this permits circular prefill definitions.
        ;; good enough to warn "Circular prefill definitions not recommended" ?
        prefill-options (->> association-options
                             (filter (fn [{:keys [value]}]
                                       (contains? current-associations-set value)))
                             (remove (fn [{:keys [value]}]
                                       (= value current-association-key)))
                             (into [{:value nil :label (loc/tr :none)}]))]
    [:div
     {:class (uu/cm "w-[540px]")}
     [:div
      {:class (uu/cm "font-bold mb-1")}
      (loc/tr :creator-config/prefill)]
     [:div
      {:class (uu/cm "my-1")}
      [:div
       (loc/tr :creator-config/prefilled-from)]
      [select/view
       {:value selected-prefill
        :placeholder (loc/tr :none)
        :options prefill-options
        :on-change (fn [val]
                     (swap! state assoc :prefill val))}]]
     [:div
      {:class (uu/cm "flex justify-between mt-2")}
        ;;footer
      [button/secondary-hollow
       {:on-click close-popover!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:on-click (fn [_]
                    (rf/dispatch [m/update-association-prefill
                                  {:index index
                                   :config-path config-path
                                   :prefill selected-prefill}])
                    (close-popover!))
        :disabled? (= selected-prefill current-prefill)}
       (loc/tr :save)]]]))

;; simpler to compare and modify
;; TODO currently only works for stages
(defn simplified-visibility
  [association-config visibility-key]
  (->> (get association-config visibility-key)
       (mapv (fn [[k v]]
               [k (:setting v)]))
       (into {})
       (assoc {} visibility-key)))

(defn visibility-popover
  [{:keys [state close-popover! association-config index config-path]}]
  (let [visibility-state (r/cursor state [:visibility])
        stages @(rf/subscribe [m/stages])
        visibility-key (if (seq stages)
                         :stages
                         :create)
        current-visibility (simplified-visibility association-config
                                                  visibility-key)
        visibility @visibility-state]
    [:div
     {:class (uu/cm "w-[540px]")}
     [:div
      {:class (uu/cm "font-bold mb-1")}
      (loc/tr :creator-config/visibility-requirements)]
     ;;note: if used for non-stages specific, use one select-box
     [:div
      {:class (uu/cm "flex flex-col gap-2")}
      (doall
       (for [stage-pair (partition-all 2 2 stages)]
         ^{:key (-> stage-pair first :db/id str)}
         [:div
          {:class (uu/cm "flex justify-between gap-3")}
          (doall
           (for [{:keys [db/id] :as stage} stage-pair
                 :let [setting (get-in @visibility-state [:stages id])
                       disabled? (u/inherited-mandatory? (get visibility visibility-key)
                                                         stages
                                                         stage)]]
             ^{:key (str id)}
             [:div
              {:class "w-full flex flex-col justify-end"}
              [:div
               {:class (uu/cm "my-1")}
               (entity/display-string stage)]
              [:div
               {:class (uu/cm (when disabled?
                                "text-disabled-light-text cursor-not-allowed"))}
               [select/view
                {:value setting
                 :placeholder (loc/tr :select)
                 ;;TODO UI doesn't indicate disabled => use data-input(?)
                 ;;     or add manual class-names(?)
                 ;;     + add tooltip "inherited from earlier stage" etc
                 :disabled? disabled?
                 :options (->> [:mandatory
                                :optional
                                :not-shown]
                               (mapv (fn [k]
                                       {:value k
                                        :label (loc/tr k)})))
                 :on-change (fn [setting]
                              (set-and-inherit! {:state visibility-state
                                                 :stages stages
                                                 :stage stage
                                                 :new-setting setting}))}]]]))]))]
     [:div
      {:class (uu/cm "flex justify-between mt-2")}
      [button/secondary-hollow
       {:on-click close-popover!}
       (loc/tr :cancel)]
      [button/primary-solid
       {:on-click (fn [_]
                    (rf/dispatch [m/update-association-visibility
                                  {:index index
                                   :config-path config-path
                                   :visibility visibility}])
                    (close-popover!))
        :disabled? (= visibility current-visibility)}
       (loc/tr :save)]]]))

(defn association-actions
  [{:keys [config-path association-config index stages]}]
  (r/with-let [state (r/atom {})
               close-prefill! (fn []
                                (swap! state dissoc :prefill-open?))
               close-visibility! (fn []
                                   (swap! state dissoc :visibility-open?))]
    (let [{:keys [prefill-open? visibility-open?]} @state
          actions [{:label
                    [:span
                     (loc/tr :creator-config/visibility-settings)
                     [:i
                      {:class (uu/cm "fa fa-chevron-down"
                                     "ml-2")}]]
                    :on-click (fn [_]
                                (js/setTimeout
                                 (fn [_]
                                   (let [visibility-key (if (seq stages)
                                                          :stages
                                                          :create)
                                         visibility (simplified-visibility
                                                     association-config
                                                     visibility-key)]
                                     (swap! state assoc
                                            :visibility-open? true
                                            :visibility visibility)))
                                 10))}
                   {:label
                    [:span
                     (loc/tr :creator-config/prefill-settings)
                     [:i
                      {:class (uu/cm "fa fa-chevron-down"
                                     "ml-2")}]]
                    :on-click (fn [_]
                                ;;on-open-change seems to close this automatically
                                ;; without this timeout:
                                (js/setTimeout
                                 (fn [_]
                                   (let [prefill-from (:prefill-from association-config)]
                                     (swap! state assoc
                                            :prefill-open? true
                                            :prefill prefill-from)))
                                 10))}
                   {:label
                    [:span
                     [:i
                      {:class (uu/cm "fa fa-trash-alt"
                                     "mr-2")}]
                     (loc/tr :remove)]
                    :on-click (fn [_]
                                (rf/dispatch [m/remove-association
                                              {:config-path config-path
                                               :index index}]))}]]
      [:td
       {:class (uu/cm "w-[44px]"
                      "bg-white"
                      "sticky #_right-0 z-20 w-10 px-1 py-2"
                      "lg:-right-6 -right-4")}
       (when prefill-open?
         [popover/view
          {:open? prefill-open?
           :modal? true
           :gray-out-background? true
           :content-class (uu/cm "overflow-auto"
                                 "max-h-[var(--radix-popover-content-available-height)]")
           :direction :bottom-left
           :on-open-change (fn [val]
                             (swap! state assoc :prefill-open? val))}
          nil
          [prefill-popover {:state state
                            :association-config association-config
                            :index index
                            :close-popover! close-prefill!}]])
       (when visibility-open?
         [popover/view
          {:open? visibility-open?
           :modal? true
           :gray-out-background? true
           :content-class (uu/cm "overflow-auto"
                                 "max-h-[var(--radix-popover-content-available-height)]")
           :direction :bottom-left
           :on-open-change (fn [val]
                             (swap! state assoc :prefill-open? val))}
          nil
          [visibility-popover {:state state
                               :config-path config-path
                               :association-config association-config
                               :index index
                               :close-popover! close-visibility!}]])

       (if (empty? actions)
         [:i
          {:class (uu/cm "fa fa-ellipsis-v cursor-not-allowed min-w-4 text-center"
                         "w-8 h-8 flex items-center justify-center"
                         "text-disabled-regular-text")}]
         [dropdown/view
          {:trigger
           [:i
            {:class (uu/cm "fa fa-ellipsis-v"
                           "w-8 h-8 flex justify-center items-center rounded"
                           "hover:bg-base-regular hover:text-base-light-text-paler")}]
           :items
           actions}])])))

(defn resolve-association-entity-types
  []
  (let [pipeline @(rf/subscribe [m/pipeline])
        entity-type @(rf/subscribe [m/entity-type])
        target-type @(rf/subscribe [m/target-type])
        target-profile-type (ecu/resolve-target-profile-type
                             {:pipeline pipeline
                              :entity-type entity-type
                              :target-type target-type})]
    (cond-> #{entity-type}
      (keyword? target-type)
      (conj target-type)

      (keyword? target-profile-type)
      (conj target-profile-type))))

(defn association-item
  [{:keys [association-config config-path index] :as _args}]
  (r/with-let [ref (atom nil)
               open? (r/atom false)]
    (let [{:keys [association-key
                  prefill-from]} association-config
          entity-type @(rf/subscribe [m/entity-type])
          target-type @(rf/subscribe [m/target-type])
          association-entity-types (resolve-association-entity-types)
          prefill-label (if prefill-from
                          (u/association-key-label
                           {:only-entity-type? (not target-type)}
                           prefill-from)
                          (loc/tr :none))
          prefill-full-label (loc/tr :creator-config/prefill-colon
                                     [prefill-label])
          [other-et _ association-type] association-key
          et-mismatch? (not (contains? association-entity-types
                                       other-et))
          target-label (case entity-type
                         :entity.type/fundraising-opportunity (loc/tr :investor-lc)
                         :entity.type/sales-opportunity (loc/tr :customer-lc)
                         :entity.type/oppo (loc/tr :target-asset-lc)
                         :entity.type/exit-opportunity (loc/tr :buyer-lc)
                         (loc/tr :target-lc))
          et-mismatch-tooltip (loc/tr :creator-config/et-mismatch-tooltip
                                      [target-label])
          stages @(rf/subscribe [m/stages])]
      [:<>
       [:td
        {:class
         (uu/cm "cursor-move"
                "bg-white"
                "sticky z-20 px-1"
                "lg:-left-6 -left-4"
                "w-6 min-w-6"
                "lg:w-8 lg:min-w-8")}
        [:i
         {:class (uu/cm "fal fa-grip-vertical"
                        "w-full" "ml-2")}]]
       [:td
        {:class
         (uu/cm "justify-between"
                "items-center"
                "pl-8"
                "sticky left-2 z-20 px-4"
                "min-w-64"
                "bg-white"
                "border-r")}
        [:div
         {:class "flex py-3 items-center"}
         [:div
          {:class
           (uu/cm "h-full" "flex" "items-center")}
          [:i
           {:class "fa fa-user pr-3"}]]
         [:div
          {:class (uu/cm "flex flex-col")}
          [:div
           {:class (uu/cm "font-bold")}

           (u/association-label nil other-et association-type)]
          [:div
           {:class (uu/cm "text-sm mb-0 text-base-light-text-paler")}
           " "
           prefill-full-label]]
         ;; in future:
         #_(when edit-default?
             [:div "default-value-editor"])
         (when et-mismatch?
           [tooltip/view
            {:trigger-class (uu/cm "text-disabled-regular-text"
                                   "cursor-default")}
            [:span
             [:i
              {:class (uu/cm "fal fa-exclamation-circle pl-2")}]]
            et-mismatch-tooltip])]]
       (when (seq stages)
         [association-stages-config
          {:association-config association-config
            ;;:config-path        config-path
            ;;:entity-type        entity-type
           :index              index
           :dropdown-container ref}]
          ;; TODO change 'when' above into 'if' when implementing for non-pipeline entities:
         #_[create-config
            {:field-config       field-config
             :config-path        config-path
             :entity-type        entity-type
             :index              index
             :dropdown-container ref}])
       [association-actions
        {:association-config association-config
         :config-path        config-path
         :stages stages
         :dropdown-container ref
         :index              index
         :open?              open?}]])))

(defn sortable-associations
  []
  (let [{:keys [associations-config-path]} (ecu/resolve-config-paths
                                            {:entity-type @(rf/subscribe [m/entity-type])
                                             :target-type @(rf/subscribe [m/target-type])
                                             :pipeline @(rf/subscribe [m/pipeline])})
        config @(rf/subscribe [m/edited-config {:config-path associations-config-path}])
        reorder-associations (fn [from to]
                               (rf/dispatch-sync
                                [m/reorder-associations
                                 {:config-path associations-config-path
                                  :from from :to to}]))
        associations (:associations config)]
    [sortable-table-list/view
     {:items (vec associations)
      :gap 0
      :get-key (fn [item]
                 (-> item :association-key #_str))
      :on-reorder (fn [{:keys [source-index destination-index]}]
                    (reorder-associations source-index destination-index))
      :tr-class (uu/cm "border rounded")
      :render-item (fn [{:keys [item index dragging?]}]
                     [association-item
                      {:association-config item
                       :is-dragging? dragging?
                       :index index
                       :config-path associations-config-path}])}]))

(defn associations-header
  [{:keys [stages]}]
  [:tr
   {:class
    (uu/cm "bg-base-regular rounded border")}
   [:th
    {:class (uu/cm "sticky left-0 z-20 mx-1 py-2 bg-base-regular"
                   "lg:-left-6 -left-4"
                   "w-6 min-w-6"
                   "lg:w-8 lg:min-w-8")}
    ""]
   [:th
    {:class (uu/cm "pl-8 py-2"
                   "sticky left-2 z-20 mx-4 py-2 bg-base-regular")}
    (loc/tr :association/type)]
   (if (seq stages)
     (->> stages
          (map (fn [{:keys [db/id] :as stage}]
                 (let [stage-name (entity/display-string stage)]
                   ^{:key (str "stage-" id)}
                   [:th
                    {:class (uu/cm
                             "w-32 max-w-32"
                             "items-center"
                             "text-center"
                             "text-ellipsis truncate _overflow-hidden whitespace-nowrap"
                             "px-4"
                             "h-full"
                             "cursor-default"
                             "even:bg-complement-regular")}
                    [tooltip/view
                     {:trigger-class (uu/cm
                                      "truncate"
                                      "max-w-full w-full")
                      :content-class "bg-secondary-gloomy"
                      :delay-duration 100
                      :side "top"}
                     stage-name
                     stage-name]]))))
     [:th
      {:title (loc/tr :entity-create-config/single-stage-entity-create)
       :class (uu/cm "flex-1 flex-grow flex justify-center truncate")}
      (loc/tr :entity-create-config/single-stage-entity-create)])
   [:th
    {:class (uu/cm "w-[44px]"
                   "sticky right-0 z-20 _w-10 _px-1 py-2"
                   "lg:-right-6 -right-4"
                   "bg-base-regular")}
    ""]])

(defn person-associations-config
  []
  (r/with-let [state (r/atom {})
               ;;_ (swap! state assoc :open? true)
               close-popover! (fn [_] (swap! state dissoc :open?))]
    (let [pipeline @(rf/subscribe [m/pipeline])
          target-type @(rf/subscribe [m/target-type])
          {:keys [associations-config-path]} (ecu/resolve-config-paths
                                              {:entity-type @(rf/subscribe [m/entity-type])
                                               :target-type target-type
                                               :pipeline pipeline})
          config @(rf/subscribe [m/edited-config {:config-path associations-config-path}])
          associations (:associations config)
          open? (:open? @state)
          stages @(rf/subscribe [m/stages])]
      [:div
       [:div
        {:class (uu/cm "flex justify-end items-center gap-2 mt-4")}
        (when pipeline
          [copy-config/view
           {:pipeline pipeline
            :target-type target-type
            :config-path associations-config-path}])
        [popover/view
         {:open? open?
          :modal? true
          :gray-out-background? true
          :direction :bottom-left
          :content-class (uu/cm "overflow-auto"
                                "max-h-[var(--radix-popover-content-available-height)]")
          :on-open-change (fn [val]
                            (when val
                              (init-stages! {:state state
                                             :stages stages}))

                            (swap! state assoc :open? val))}
         [button/primary-solid
          {:on-click (fn [_]
                       ;;(swap! state assoc :open? true)
                       )}
          [:i
           {:class (uu/cm "fa fa-user")}]
          (loc/tr :creator-config/add-person-association)
          [:i
           {:class (uu/cm "fa fa-chevron-down")}]]
         [person-association-popover {:state state
                                      :close-popover! close-popover!}]]]
       (if (seq associations)
         [:table
          {:class (uu/cm "mt-4"
                         "min-w-full table-fixed")}
          [:thead
           [associations-header {:stages stages}]]
          [sortable-associations]]
         [:div
          {:class (uu/cm "m-8")}
          [:div
           {:class (uu/cm "flex items-start p-4 rounded shadow-md"
                          "bg-blue-50 text-blue-800 border-blue-400"
                          "border-l-4 mb-4")}
           [:i
            {:class "fas fa-info-circle mr-4"}]
           (loc/tr :creator-config/no-associations)]])])))
